import { identity } from 'lodash-es';
import { HEADLINER_USER_SERVICE_BASEURL } from 'config';
import ServiceFactory, { returnVoid } from 'services/ServiceFactory';
import {
  MY_GOOGLE_FONTS_PATH,
  MY_PERSONAL_FONTS_PATH,
  PERSONAL_FONTS_PATH,
  SUPPORT_REQUEST_PATH,
  PERSONAL_FONT_PATH,
  MY_DISPLAY_PREFS_PATH,
  MY_USER_PREFS_PATH,
  MY_CAPTIONS_PREFS_PATH,
  SAMPLE_AUDIO_PATH,
  ANNOUNCEMENT_VIEW_CONFIRMATION_PATH,
  MY_ANNOUNCEMENTS_PATH,
  MY_ONBOARDING_PATH,
  ONBOARDING_VIEW_CONFIRMATION_PATH,
  PROJECT_TEMPLATES_PATH,
  PROJECT_TEMPLATE_PATH,
  PROJECT_TEMPLATE_COMPATIBILITY_PATH,
  PROJECT_UST_COMPATIBILITY_PATH,
  QUESTIONNAIRE_RESPONSE_PATH,
  MY_QUESTIONNAIRE_RESPONSE_PATH,
  MY_QUESTIONNAIRE_STATUS_PATH,
  VIDEO_SIZES_PATH,
  EDDY_LANGUAGES_PATH,
  MAKE_AUTOMATION_LANGUAGES_PATH,
  MAKE_CAPTION_LANGUAGES_PATH,
  WAVEFORM_PREFERENCES_PATH,
  WAVEFORM_PREFERENCE_PATH,
  MY_VIDEO_EXPORT_PREFS_PATH,
  MY_ACTIVE_EXPERIMENT_VARIANTS_PATH,
  MY_COLOR_PREFS_PATH,
  MY_NOTIFICATION_ALERT_PATH,
  USER_DISPALY_PREFS_PATH,
  MY_TEXT_PRESETS_PATH,
  MY_TEXT_PRESET_PATH,
} from './constants';
import {
  AnnouncementsResponse,
  CaptionUserPreferences,
  CreateProjectTemplateRequest,
  Questionnaire,
  CreateSupportRequestArgs,
  CreateWaveformPreferenceResponse,
  DisplayPreferencesResponse,
  Experiment,
  GoogleFontsResponse,
  LanguagesResponse,
  NotificationAlert,
  OnboardingItem,
  PersonalFontsResponse,
  ProjectTemplateCompatibility,
  ProjectTemplateUpdateRequest,
  ProjectTemplatesResponse,
  QuestionnaireStatus,
  SampleAudioResponse,
  GetTextPresetsResponse,
  UpdateColorPreferencesRequest,
  UserPreferences,
  VideoExportUserPreferencesUpdateRequest,
  VideoSizesResponse,
  WaveformUserPreferences,
  CreateTextPresetResponse,
  CreateMyTextPresetArgs,
} from './types';
import {
  AnnouncementViewConfirmationRequest,
  CreatePersonalFontArgs,
  DeleteFontArgs,
  DeleteMyTextPresetArgs,
  DeleteProjectTemplateRequest,
  DeleteWaveformPreferenceRequest,
  GetMyExperimentVariantsRequest,
  GetMyTextPresetsRequest,
  GetOnboardingItemRequest,
  GetProjectTemplateCompatibilityRequest,
  GetProjectTemplatesRequest,
  GetUSTCompatibilityRequest,
  GetUserDisplayPreferencesArgs,
  OnboardingViewConfirmationRequest,
  UpdatePersonalFontArgs,
} from './types/service';

const serviceFactory = new ServiceFactory(HEADLINER_USER_SERVICE_BASEURL);

/**
 * Retrieves personal font information the caller of the API
 *
 * This endpoint requires the Authorization header. The user id that is
 * encrypted inside of the token from the Authorization header will be used
 * to check access for various font files.
 */
export const getMyPersonalFonts = serviceFactory.createGetEndpoint(
  MY_PERSONAL_FONTS_PATH,
)<undefined, PersonalFontsResponse>();

/**
 * Retrieves all google fonts in alphabetical order
 *
 * The response includes all google fonts in alphabetical order (no pagination).
 * It is up to the caller to display and load the css in a reasonable fashion.
 * It is not wise to load all css at once since there will be over 1400 font families.
 * The css url is a smaller version that's meant just to preview the text. The
 * character set only includes the family name itself, which means that you
 * can't preview any other text than the family name itself.
 */
export const getMyGoogleFonts = serviceFactory.createGetEndpoint(
  MY_GOOGLE_FONTS_PATH,
)<undefined, GoogleFontsResponse>();

/**
 * Upload personal font files
 *
 * If both google family and font file are provided, both are processed.
 */
export const createPersonalFont = serviceFactory.createPostEndpoint(
  PERSONAL_FONTS_PATH,
)({
  data: (args: CreatePersonalFontArgs) => {
    const formData = new FormData();
    args.fontFile?.forEach((file) => {
      formData.append('fontFile', file);
    });

    if (args.googleFamily) {
      formData.append('googleFamily', args.googleFamily);
    }

    return formData;
  },
  select: returnVoid(),
});

/**
 * Send support request from app
 */
export const createSupportRequest = serviceFactory.createPostEndpoint(
  SUPPORT_REQUEST_PATH,
)<CreateSupportRequestArgs, void>({
  data: identity,
});

/**
 * Update display name of personal font access
 */
export const updateFontName = serviceFactory.createPutEndpoint(
  PERSONAL_FONT_PATH,
)<UpdatePersonalFontArgs, void>({
  data: ['displayName'],
  select: returnVoid(),
});

/**
 * Delete personal font access
 */
export const deleteFont = serviceFactory.createDeleteEndpoint(
  PERSONAL_FONT_PATH,
)<DeleteFontArgs, void>({
  select: returnVoid(),
});

/**
 * Retrieves the display preferences for the specified user
 */
export const getUserDisplayPreferences = serviceFactory.createGetEndpoint(
  USER_DISPALY_PREFS_PATH,
)<GetUserDisplayPreferencesArgs, DisplayPreferencesResponse>();

/**
 * Retrieves the display preference for the user
 *
 * This endpoint requires the Authorization header. The user id that is encrypted
 * inside of the token from the Authorization header will be used to retrieve
 * the user's display preferences.
 */
export const getMyDisplayPreferences = serviceFactory.createGetEndpoint(
  MY_DISPLAY_PREFS_PATH,
)<undefined, DisplayPreferencesResponse>();

/**
 * Retrieves the preferences for the user
 *
 * This endpoint requires the Authorization header. The user id that is encrypted
 * inside of the token from the Authorization header will be used to retrieve the
 * user's preferences.
 */
export const getMyUserPreferences = serviceFactory.createGetEndpoint(
  MY_USER_PREFS_PATH,
)<undefined, UserPreferences>();

/**
 * Update a user's caption preferences
 *
 * This endpoint requires the Authorization header.
 */
export const updateMyCaptionPreferences = serviceFactory.createPutEndpoint(
  MY_CAPTIONS_PREFS_PATH,
)<CaptionUserPreferences, void>({
  select: returnVoid(),
});

/**
 * Insert a waveform preference
 *
 * This endpoint requires the Authorization header. The user id that is encrypted
 * inside of the token from the Authorization header will be used for the insert
 * operation.
 *
 * The unique id of the resource is returned.
 */
export const createWaveformPreference = serviceFactory.createPostEndpoint(
  WAVEFORM_PREFERENCES_PATH,
)<WaveformUserPreferences, CreateWaveformPreferenceResponse>();

/**
 * Delete a waveform preference
 *
 * This endpoint requires the Authorization header.
 */
export const deleteWaveformPreference = serviceFactory.createDeleteEndpoint(
  WAVEFORM_PREFERENCE_PATH,
)<DeleteWaveformPreferenceRequest, void>({
  select: returnVoid(),
});

/**
 * Update a user's video export preferences
 *
 * This endpoint requires the Authorization header.
 */
export const updateVideoExportPreferences = serviceFactory.createPutEndpoint(
  MY_VIDEO_EXPORT_PREFS_PATH,
)<VideoExportUserPreferencesUpdateRequest, void>({
  select: returnVoid(),
});

/**
 * Update a user's color preferences
 *
 * The payload should be an array of color strings (e.g. "#abcdef"). The API is
 * agnostic with respect to the format (e.g. values like "#ff0000" and "(255,0,0)"
 * and "red" are all acceptable) although any duplicated values will be removed with
 * only the first unique value retained. The order of the list is preserved but it
 * is up to the caller to ensure that the list is maintained on a FIFO basis.
 *
 * This endpoint requires the Authorization header.
 */
export const updateColorPreferences = serviceFactory.createPutEndpoint(
  MY_COLOR_PREFS_PATH,
)<UpdateColorPreferencesRequest, void>({
  select: returnVoid(),
});

/**
 * Retrieves all sample audio
 */
export const getSampleAudio = serviceFactory.createGetEndpoint(
  SAMPLE_AUDIO_PATH,
)<undefined, SampleAudioResponse>();

/**
 * Confirm view of an announcement
 *
 * Does not follow typical REST API pattern. This endpoint will confirm that the
 * user has viewed the announcement and doesn't actually "get" any resource. Calling
 * this endpoint with the same parameter will not do anything. We only record the
 * first time this endpoint was called for a specific user/announcement combination
 */
export const confirmAnnouncementViewed = serviceFactory.createGetEndpoint(
  ANNOUNCEMENT_VIEW_CONFIRMATION_PATH,
)<AnnouncementViewConfirmationRequest, void>({
  queryParams: identity,
  select: returnVoid(),
});

/**
 * Retrieves all active/valid announcements
 *
 * Retrieves all active/valid announcements to show for the user associated with
 * the token provided
 */
export const getMyAnnouncements = serviceFactory.createGetEndpoint(
  MY_ANNOUNCEMENTS_PATH,
)<undefined, AnnouncementsResponse>();

/**
 * Retrieves onboarding viewed confirmation
 *
 * For the given token, the user's viewed confirmation for the given onboarding
 * identifier is returned. The name of the onboarding type can be found via the
 * GET endpoint for onboarding types
 */
export const getMyOnboardingItem = serviceFactory.createGetEndpoint(
  MY_ONBOARDING_PATH,
)<GetOnboardingItemRequest, OnboardingItem>({
  queryParams: identity,
});

/**
 * Confirm view of an onboarding tutorial
 *
 * Does not follow typical REST API pattern. This endpoint will confirm that the
 * user has viewed the onboarding tutorial and doesn't actually "get" any resource.
 * Calling this endpoint with the same parameter will not do anything. We only record
 * the first time this endpoint was called for a specific user/announcement combination
 */
export const confirmOnboardingTutorialViewed = serviceFactory.createGetEndpoint(
  ONBOARDING_VIEW_CONFIRMATION_PATH,
)<OnboardingViewConfirmationRequest, void>({
  queryParams: identity,
  select: returnVoid(),
});

/**
 * Get all project templates
 *
 * For a given user, returns all project templates to display in the app
 */
export const getProjectTemplates = serviceFactory.createGetEndpoint(
  PROJECT_TEMPLATES_PATH,
)<GetProjectTemplatesRequest, ProjectTemplatesResponse>();

/**
 * Create a project template
 */
export const createProjectTemplate = serviceFactory.createPostEndpoint(
  PROJECT_TEMPLATES_PATH,
)<CreateProjectTemplateRequest, void>({
  select: returnVoid(),
});

/**
 * Update project template entry
 */
export const updateProjectTemplate = serviceFactory.createPutEndpoint(
  PROJECT_TEMPLATE_PATH,
)<ProjectTemplateUpdateRequest, void>({
  select: returnVoid(),
});

/**
 * Delete project template
 */
export const deleteProjectTemplate = serviceFactory.createDeleteEndpoint(
  PROJECT_TEMPLATE_PATH,
)<DeleteProjectTemplateRequest, void>({
  select: returnVoid(),
});

/**
 * Get project template compatibility by widget configuration id
 */
export const getProjectTemplateCompatibility = serviceFactory.createGetEndpoint(
  PROJECT_TEMPLATE_COMPATIBILITY_PATH,
)<GetProjectTemplateCompatibilityRequest, ProjectTemplateCompatibility>();

/**
 * Get project template compatibility by user generated template id
 */
export const getProjectUSTCompatibility = serviceFactory.createGetEndpoint(
  PROJECT_UST_COMPATIBILITY_PATH,
)<GetUSTCompatibilityRequest, ProjectTemplateCompatibility>();

/**
 * Create a response to questions in a questionnaire
 */
export const createQuestionnaireResponse = serviceFactory.createPostEndpoint(
  QUESTIONNAIRE_RESPONSE_PATH,
)<Questionnaire, void>({
  select: returnVoid(),
});

/**
 * Get question responses from a user
 */
export const getMyQuestionnaireResponse = serviceFactory.createGetEndpoint(
  MY_QUESTIONNAIRE_RESPONSE_PATH,
)<undefined, Questionnaire>();

/**
 * Gets overall questionare status of the user
 */
export const getMyQuestionnaireStatus = serviceFactory.createGetEndpoint(
  MY_QUESTIONNAIRE_STATUS_PATH,
)<undefined, QuestionnaireStatus>();

/**
 * Retrieves the actual video sizes corresponding to video export
 */
export const getVideoExportSizes = serviceFactory.createGetEndpoint(
  VIDEO_SIZES_PATH,
)<undefined, VideoSizesResponse>();

/**
 * Retrieves the list of supported languages for Eddy projects
 */
export const getEddySupportedProjectLanguages =
  serviceFactory.createGetEndpoint(EDDY_LANGUAGES_PATH)<
    undefined,
    LanguagesResponse
  >();

/**
 * Retrieves the list of supported languages for Make automations
 */
export const getMakeAutomationSupportedLanguages =
  serviceFactory.createGetEndpoint(MAKE_AUTOMATION_LANGUAGES_PATH)<
    undefined,
    LanguagesResponse
  >();

/**
 * Retrieves the list of supported languages for Make captoins
 */
export const getMakeCaptionSupportedLangauges =
  serviceFactory.createGetEndpoint(MAKE_CAPTION_LANGUAGES_PATH)<
    undefined,
    LanguagesResponse
  >();

/**
 * Assigning/retrieving a user's active experiments and variants
 *
 * A call to this endpoint for the specified experiments will either
 * (1) randomly assign the user to a variant of a given experiment or else
 * (2) retrieve the user’s variant if already assigned. Either the authorization
 * header, which includes the user information or the anonymousUserToken must be
 * sent with the request. If neither field issent, the api will return an error.
 * If both fields are sent in the case where an anonymous user logs in at a later
 * time, then the user= will retain the variant they were assigned while anonymous
 * and use that going forward.
 */
export const getMyActiveExperimentVariants = serviceFactory.createGetEndpoint(
  MY_ACTIVE_EXPERIMENT_VARIANTS_PATH,
)<GetMyExperimentVariantsRequest, Experiment>({
  queryParams: identity,
});

/**
 * Retrieves text preset information
 */
export const getMyTextPresets = serviceFactory.createGetEndpoint(
  MY_TEXT_PRESETS_PATH,
)<GetMyTextPresetsRequest, GetTextPresetsResponse>({
  queryParams: identity,
});

/**
 * Create text preset for user
 */
export const createMyTextPreset = serviceFactory.createPostEndpoint(
  MY_TEXT_PRESETS_PATH,
)<CreateMyTextPresetArgs, CreateTextPresetResponse>();

/**
 * Delete text preset for user
 */
export const deleteMyTextPreset = serviceFactory.createDeleteEndpoint(
  MY_TEXT_PRESET_PATH,
)<DeleteMyTextPresetArgs, void>({
  select: returnVoid(),
});

/**
 * Retrieves notification alerts for the user
 */
export const getMyNotificationAlert = serviceFactory.createGetEndpoint(
  MY_NOTIFICATION_ALERT_PATH,
)<undefined, NotificationAlert>();

/**
 * Updates a user's notification alerts
 */
export const updateNotificationAlert = serviceFactory.createPutEndpoint(
  MY_NOTIFICATION_ALERT_PATH,
)<NotificationAlert, void>({
  select: returnVoid(),
});
